import { Injectable, signal } from '@angular/core';
import { surrYearsLabel } from '../models/product-search.models';

/** Carrier/products picker value used by the products filter chrome.
 *  Mirrors the shape used by /rates' CompanyFilterValue so the shared
 *  <app-company-product-filter> component binds the same way. */
export interface ProductCompanyFilterValue {
  companies: string[];
  products: string[];
}

/**
 * Menu-axis Phase 4.1 — single global filter slot for /products.
 *
 * Unlike /rates (per-ProductType Records keyed by activeTab), /products
 * is cross-product by design — `activeTypes` is a multi-select Set of
 * the product types currently visible. There is no per-tab archiving
 * because there are no tabs. One slot, one state.
 *
 * Fields lifted from products.component.ts §"Filter State" (lines
 * 252-298) — 10 dimensions covering type / company / amBest / state /
 * status / surrYears / shareClasses / premiumTypes / riders / search.
 */
export interface ProductFilterSlot {
  /** Multi-select product types — pills toggle inclusion in this Set. */
  activeTypes: Set<string>;
  /** Carrier/products picker (rendered via <app-company-product-filter>). */
  companyFilter: ProductCompanyFilterValue | null;
  /** AM Best multi-select. */
  selectedAmBest: string[];
  /** State availability multi-select — matches the rates rail (2026-06-12, #7).
   *  The Criteria State knob writes a single client state into this array; the
   *  Advanced "State Availability" picker is multi-select over the same field. */
  selectedStates: string[];
  /** Status filter — 'all' | 'open' | 'closed'. */
  filterStatus: 'all' | 'open' | 'closed';
  /** Surrender-years multi-select. */
  selectedSurrYears: string[];
  /**
   * The premium the user intends to place. A product is kept when its own band
   * admits that amount — minPremium ≤ value ≤ maxPremium. null = no restriction.
   *
   * Replaced a pair of Min/Max Premium sliders (2026-09-24). Those were never a
   * range over product values either: each carried the same "amount I intend to
   * place" and merely tested it against a different bound, so the pair only did
   * anything useful when set to the same number. One box says that directly,
   * and matches the Premium filter on /rates (`contributionAmount`).
   */
  selectedPremium: number | null;
  /** Share-class multi-select. */
  selectedShareClasses: string[];
  /** Premium type multi-select (Single / Flexible / Both). */
  selectedPremiumTypes: string[];
  /** Riders multi-select (ROP / MVA / GMWB / GMIB / GMAB / Bonus / DB / Fee-Based). */
  selectedRiders: string[];
  /** Search text. */
  searchText: string;
}

/** Factory for an empty product filter slot — same defaults the
 *  component used at init. */
export function emptyProductFilterSlot(): ProductFilterSlot {
  return {
    activeTypes: new Set<string>(['FA', 'IA', 'VA', 'RILA']),
    companyFilter: null,
    selectedAmBest: [],
    selectedStates: [],
    filterStatus: 'open',
    selectedSurrYears: [],
    selectedPremium: null,
    selectedShareClasses: [],
    selectedPremiumTypes: [],
    selectedRiders: [],
    searchText: '',
  };
}

/** Compact currency for chips + slider readouts — "$25K" / "$2.5M". */
export function formatPremium(v: number): string {
  if (v >= 1_000_000) {
    const m = v / 1_000_000;
    return '$' + (Number.isInteger(m) ? m : Number(m.toFixed(1))) + 'M';
  }
  if (v >= 1_000) {
    const k = v / 1_000;
    return '$' + (Number.isInteger(k) ? k : Number(k.toFixed(1))) + 'K';
  }
  return '$' + v.toLocaleString();
}

export interface AppliedChip {
  key: string;
  label: string;
  value: string;
  isMono?: boolean;
}

/**
 * Menu-axis Phase 4.1 — ProductsFilterStateService.
 *
 * Owns the global filter slot for /products. Both the legacy chrome
 * (via ProductsComponent's proxy getter/setters) and the future
 * ProductSearchRailComponent (Phase 4.2) bind to this service.
 *
 * Counter signals (requestSearchApply / requestApplyFilters /
 * requestShelfToggle) mirror the patterns from RatesFilterStateService
 * (3c.1, 3d.1, 3f.1). The component constructor effect dispatches each.
 */
@Injectable({ providedIn: 'root' })
export class ProductsFilterStateService {
  /** Change-notification signal — bumps on every state mutation.
   *  Downstream consumers (rail computed signals for pill counts,
   *  chip stack, active toggles, etc.) subscribe via the version()
   *  read. */
  private _version = signal(0);
  readonly version = this._version.asReadonly();
  private _bump(): void {
    this._version.update((n) => n + 1);
  }

  /** Single global filter slot. */
  private _slot: ProductFilterSlot = emptyProductFilterSlot();

  /** Whole-slot read — rail computed signals re-evaluate on version()
   *  changes; this getter returns a stable reference to the live slot. */
  get slot(): ProductFilterSlot {
    return this._slot;
  }

  setSlotField<K extends keyof ProductFilterSlot>(key: K, value: ProductFilterSlot[K]): void {
    this._slot[key] = value;
    this._bump();
  }

  // ── 10 getter/setter pairs proxied through the slot ───────────────
  // Mirrors the rates 3a.3 pattern: the component's field accessors
  // delegate to these so cross-chrome sync is by construction.

  get activeTypes(): Set<string> { return this._slot.activeTypes; }
  set activeTypes(v: Set<string>) { this._slot.activeTypes = v; this._bump(); }

  get companyFilter(): ProductCompanyFilterValue | null { return this._slot.companyFilter; }
  set companyFilter(v: ProductCompanyFilterValue | null) { this._slot.companyFilter = v; this._bump(); }

  get selectedAmBest(): string[] { return this._slot.selectedAmBest; }
  set selectedAmBest(v: string[]) { this._slot.selectedAmBest = v; this._bump(); }

  get selectedStates(): string[] { return this._slot.selectedStates; }
  set selectedStates(v: string[]) { this._slot.selectedStates = v; this._bump(); }

  get filterStatus(): 'all' | 'open' | 'closed' { return this._slot.filterStatus; }
  set filterStatus(v: 'all' | 'open' | 'closed') { this._slot.filterStatus = v; this._bump(); }

  get selectedSurrYears(): string[] { return this._slot.selectedSurrYears; }
  set selectedSurrYears(v: string[]) { this._slot.selectedSurrYears = v; this._bump(); }

  get selectedPremium(): number | null { return this._slot.selectedPremium; }
  set selectedPremium(v: number | null) { this._slot.selectedPremium = v; this._bump(); }

  get selectedShareClasses(): string[] { return this._slot.selectedShareClasses; }
  set selectedShareClasses(v: string[]) { this._slot.selectedShareClasses = v; this._bump(); }

  get selectedPremiumTypes(): string[] { return this._slot.selectedPremiumTypes; }
  set selectedPremiumTypes(v: string[]) { this._slot.selectedPremiumTypes = v; this._bump(); }

  get selectedRiders(): string[] { return this._slot.selectedRiders; }
  set selectedRiders(v: string[]) { this._slot.selectedRiders = v; this._bump(); }

  get searchText(): string { return this._slot.searchText; }
  set searchText(v: string) { this._slot.searchText = v; this._bump(); }

  // ── Filter option lists (Phase 12) ──────────────────────────────
  // The rail's inline pickers need option lists. Static ones are seeded
  // here; the dynamic ones (surrYears / shareClass / state) are derived
  // from product data by ProductsComponent, which pushes them via
  // setFilterOptions() on data load. The rail reads filterOptions()[dim].
  private _filterOptions = signal<Record<string, string[]>>({
    amBest:      ['A++', 'A+', 'A', 'A-', 'B++', 'B+', 'B', 'B-'],
    premiumType: ['Single', 'Flexible', 'Both'],
    riders:      ['ROP', 'MVA', 'GMWB', 'GMIB', 'GMAB', 'DB', 'Fee-Based'],
    surrYears:   [],
    shareClass:  [],
    state:       [],
    carriers:    [],
  });
  readonly filterOptions = this._filterOptions.asReadonly();

  /** Merge dynamic option lists derived from product data. Called by
   *  ProductsComponent when its filtered dataset (and thus the available
   *  surrYears / shareClass / state / carrier values) changes. */
  setFilterOptions(partial: Record<string, string[]>): void {
    this._filterOptions.update((cur) => ({ ...cur, ...partial }));
  }

  /** Carrier → products tree, pushed by ProductsComponent on data load.
   *  Drives the CompanyProductPicker popover (Miller-columns v1). Empty
   *  by default until the page has loaded its dataset. */
  private _carrierProductTree = signal<Record<string, string[]>>({});
  readonly carrierProductTree = this._carrierProductTree.asReadonly();
  setCarrierProductTree(tree: Record<string, string[]>): void {
    this._carrierProductTree.set(tree);
  }

  // ── Request signals (no-ack counters; component effects dispatch) ──

  /** Bumped by the rail's search input. Component effect debounces
   *  150ms then applies. Mirrors RatesFilterStateService.requestSearchApply
   *  signature (string param so the component effect can pull the latest
   *  value without re-reading the slot). */
  searchApplyRequest = signal<string | null>(null);
  requestSearchApply(value: string): void {
    this._slot.searchText = value;
    this._bump();
    this.searchApplyRequest.set(value);
  }
  ackSearchApply(): void {
    this.searchApplyRequest.set(null);
  }

  /** Bumped by chip clears / toggle clicks / rail mutations that need
   *  the component to re-run applyFilters. Counter pattern (no-ack);
   *  effect re-fires on every increment. */
  applyFiltersRequest = signal(0);
  requestApplyFilters(): void {
    this.applyFiltersRequest.update((n) => n + 1);
  }

  // ── Case-as-saved-search (P3) ────────────────────────────────────
  // The Criteria rail section's "Reset" bumps this; the component re-seeds
  // the products filters from the loaded case. Counter pattern, no-ack.
  seedFromCaseRequest = signal(0);
  requestSeedFromCase(): void { this.seedFromCaseRequest.update((n) => n + 1); }

  // Post-filter / total product counts, published by the component's
  // updateCounts() so the rail Criteria "X of Y fit" can read them.
  private _displayedCount = signal(0);
  private _totalCount = signal(0);
  readonly displayedCount = this._displayedCount.asReadonly();
  readonly totalCount = this._totalCount.asReadonly();
  setCounts(displayed: number, total: number): void {
    this._displayedCount.set(displayed);
    this._totalCount.set(total);
  }

  // Count of products matching the loaded case (the "fit" number) — equals
  // displayedCount in Fit mode; in Show-all mode it's the matching subset among
  // all shown products. Published by updateCounts().
  private _caseFitCount = signal(0);
  readonly caseFitCount = this._caseFitCount.asReadonly();
  setCaseFitCount(n: number): void {
    this._caseFitCount.set(n);
  }


  /** Bumped by the rail's My Shelf toggle. Component effect calls
   *  sessionCtx.toggleShelfMode() + applyFilters() + refreshCells. */
  shelfToggleRequest = signal(0);
  requestShelfToggle(): void {
    this.shelfToggleRequest.update((n) => n + 1);
  }

  // ── Per-dimension clear (chip × button) ──────────────────────────

  /** Bumps whenever clearAppliedDimension() runs (i.e. an Active-Filters
   *  footprint chip is cleared). The rail + pickers listen so they can
   *  collapse that dimension's expanded section. Carries the cleared key +
   *  a monotonic tick. */
  private _dimClearedRequest = signal<{ key: string; tick: number }>({ key: '', tick: 0 });
  readonly dimClearedRequest = this._dimClearedRequest.asReadonly();

  /** Clears one filter dimension. The rail's chip × button calls this,
   *  then requestApplyFilters() to re-run the grid. */
  clearAppliedDimension(key: string): void {
    this._dimClearedRequest.update((r) => ({ key, tick: r.tick + 1 }));
    switch (key) {
      case 'type':       this._slot.activeTypes = new Set(['FA', 'IA', 'VA', 'RILA']); break;
      case 'company':    this._slot.companyFilter = null; break;
      case 'am-best':    this._slot.selectedAmBest = []; break;
      case 'state':      this._slot.selectedStates = []; break;
      case 'status':     this._slot.filterStatus = 'all'; break;
      case 'surr':       this._slot.selectedSurrYears = []; break;
      case 'premium':    this._slot.selectedPremium = null; break;
      case 'share-class':this._slot.selectedShareClasses = []; break;
      case 'prem-type':  this._slot.selectedPremiumTypes = []; break;
      case 'riders':     this._slot.selectedRiders = []; break;
      case 'search':     this._slot.searchText = ''; break;
      default: return; // unknown key: defensive no-op
    }
    this._bump();
  }

  countActiveFilters(): number {
    return countActiveProductFilters(this._slot);
  }
}

/** Pure helper for building the rail's Applied chip stack from a
 *  product filter slot. Exposed at module level so the rail component
 *  can compute chips inline via signal(). */
export function buildAppliedProductChips(slot: ProductFilterSlot): AppliedChip[] {
  const chips: AppliedChip[] = [];

  // Type pills — only chip when fewer than 4 types selected.
  if (slot.activeTypes.size > 0 && slot.activeTypes.size < 4) {
    chips.push({
      key: 'type',
      label: 'Type',
      value: Array.from(slot.activeTypes).sort().join(', '),
    });
  }

  if (slot.selectedAmBest.length > 0) {
    chips.push({
      key: 'am-best',
      label: 'AM Best',
      value: slot.selectedAmBest.length === 1
        ? slot.selectedAmBest[0]
        : `${slot.selectedAmBest.length} ratings`,
    });
  }

  if (slot.selectedStates.length) {
    chips.push({
      key: 'state',
      label: 'State',
      value: slot.selectedStates.length === 1
        ? slot.selectedStates[0]
        : `${slot.selectedStates.length} states`,
    });
  }

  if (slot.filterStatus !== 'all') {
    chips.push({
      key: 'status',
      label: 'Status',
      value: slot.filterStatus === 'open' ? 'Open' : 'Closed',
    });
  }

  if (slot.selectedSurrYears.length > 0) {
    chips.push({
      key: 'surr',
      label: 'Surrender Years',
      value: slot.selectedSurrYears.length === 1
        ? surrYearsLabel(slot.selectedSurrYears[0])
        : `${slot.selectedSurrYears.length} years`,
      isMono: true,
    });
  }

  if (slot.selectedPremium != null) {
    chips.push({
      key: 'premium',
      label: 'Premium',
      value: formatPremium(slot.selectedPremium),
      isMono: true,
    });
  }

  if (slot.selectedShareClasses.length > 0) {
    chips.push({
      key: 'share-class',
      label: 'Share Class',
      value: slot.selectedShareClasses.length === 1
        ? slot.selectedShareClasses[0]
        : `${slot.selectedShareClasses.length} classes`,
    });
  }

  if (slot.selectedPremiumTypes.length > 0) {
    chips.push({
      key: 'prem-type',
      label: 'Premium Type',
      value: slot.selectedPremiumTypes.length === 1
        ? slot.selectedPremiumTypes[0]
        : `${slot.selectedPremiumTypes.length} types`,
    });
  }

  if (slot.selectedRiders.length > 0) {
    chips.push({
      key: 'riders',
      label: 'Riders',
      value: slot.selectedRiders.length === 1
        ? slot.selectedRiders[0]
        : `${slot.selectedRiders.length} riders`,
    });
  }

  return chips;
}

/** Counts active filter dimensions on a slot — used by the rail pill
 *  badge and the legacy "filters applied" count. */
export function countActiveProductFilters(slot: ProductFilterSlot): number {
  let n = 0;
  if (slot.activeTypes.size > 0 && slot.activeTypes.size < 4) n++;
  if (slot.companyFilter && (slot.companyFilter.companies.length > 0 || slot.companyFilter.products.length > 0)) n++;
  if (slot.selectedAmBest.length > 0) n++;
  if (slot.selectedStates.length > 0) n++;
  if (slot.filterStatus !== 'all') n++;
  if (slot.selectedSurrYears.length > 0) n++;
  if (slot.selectedPremium != null) n++;
  if (slot.selectedShareClasses.length > 0) n++;
  if (slot.selectedPremiumTypes.length > 0) n++;
  if (slot.selectedRiders.length > 0) n++;
  return n;
}
